import { Tabs, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import {
  getCancelledRefinancesThunk,
  getOpenRefinancesThunk,
  getPendingRefinancesThunk,
  getReferbackRefinancesThunk,
  getRejectedRefinancesThunk,
} from '../../../services/refinanceService/refinanceThunk';
import { AppDispatch, RootState } from '../../../store/store';
import CancelledRefinanceListing from './CancelledRefinanceListing';
import PendingRefinanceListing from './PendingRefinanceListing';
import ReferBackRefinanceListing from './ReferBackRefinanceListing';
import RejectedRefinanceListing from './RejectedRefinanceListing';

import type { TabsProps } from 'antd';
import OpenRefinanceListing from './OpenRefinanceListing';

const RefinanceVerifyingListingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  // const [title, setTitle] = useState("Pending");
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const [searchFiled, setSearchFiled] = useState('applicationNo');

  const {
    openRefinanceList,
    pendingRefinanceList,
    referBackRefinanceList,
    rejectedRefinanceList,
    cancelledRefinanceList,
  } = useSelector((state: RootState) => state.refinance);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {
        [searchFiled]: '',
      },
      filters: {},
    }),
    []
  );

  const items: TabsProps['items'] = [
    {
      key: 'open',
      label: (
        <span>
          Open{' '}
          {openRefinanceList?.data?.searchTotal != null && (
            <Tag>{openRefinanceList.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <OpenRefinanceListing />,
    },
    {
      key: 'pending',
      label: (
        <span>
          Pending{' '}
          {pendingRefinanceList?.data?.searchTotal != null && (
            <Tag>{pendingRefinanceList.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <PendingRefinanceListing />,
    },
    {
      key: 'referBack',
      label: (
        <span>
          Refer-back{' '}
          {referBackRefinanceList?.data?.searchTotal != null && (
            <Tag>{referBackRefinanceList.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <ReferBackRefinanceListing />,
    },
    {
      key: 'rejected',
      label: (
        <span>
          Rejected{' '}
          {rejectedRefinanceList?.data?.searchTotal != null && (
            <Tag>{rejectedRefinanceList.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <RejectedRefinanceListing />,
    },
    {
      key: 'cancelled',
      label: (
        <span>
          Cancelled{' '}
          {cancelledRefinanceList?.data?.searchTotal != null && (
            <Tag>{cancelledRefinanceList.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <CancelledRefinanceListing />,
    },
  ];

  const onTabChange = (key: string) => {
    if (key === 'open') {
      dispatch(getOpenRefinancesThunk({ ...initialParams, sortField: 'createdAt' }));
    } else if (key === 'pending') {
      dispatch(getPendingRefinancesThunk(initialParams));
    } else if (key === 'referBack') {
      dispatch(getReferbackRefinancesThunk(initialParams));
    } else if (key === 'rejected') {
      dispatch(getRejectedRefinancesThunk(initialParams));
    } else if (key === 'cancelled') {
      dispatch(getCancelledRefinancesThunk(initialParams));
    }

    queryParams.set('tab', key);
    queryParams.set('pageNum', `${initialParams.pagination.current}`);
    queryParams.set('pageSize', `${initialParams.pagination.pageSize}`);

    const newSearch = queryParams.toString();

    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    dispatch(getOpenRefinancesThunk({ ...params, sortField: 'createdAt' }));
    dispatch(getPendingRefinancesThunk(params));
    dispatch(getReferbackRefinancesThunk(params));
    dispatch(getRejectedRefinancesThunk(params));
    dispatch(getCancelledRefinancesThunk(params));
  });

  return (
    <>
      <Helmet>
        <title>{`Refinance Listing - redCASH CEP`}</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Verifying Refinance" />
      <Tabs defaultActiveKey={activeTab ?? 'pending'} items={items} onChange={onTabChange} />
    </>
  );
};

export default RefinanceVerifyingListingPage;
